import {useMutation, UseMutationResult} from '@tanstack/react-query';
import {useLocale} from 'state/locale';
import Api from 'types/api';
import {toSnakeCase} from 'utils/object';
import {request} from 'utils/request-axios';

import {
    ClientContactsArchiveApiRequest,
    ClientContactsDeleteOrganizationApiRequest,
    ClientContactsRemoveContactApiRequest,
    ClientContactsSetDefaultApiRequest,
} from './types';

export type UsePostArchiveErrorResponse = Api.Response.Failure;

export type UsePostArchiveProps = {
    formData: ClientContactsArchiveApiRequest;
    mid: string;
};

export type UsePostArchiveSuccessResponse =
    Api.Response.Template.SuccessBasicMessage;

export const usePostArchive = (): UseMutationResult<
    UsePostArchiveSuccessResponse,
    UsePostArchiveErrorResponse,
    UsePostArchiveProps
> => {
    const locale = useLocale();

    return useMutation({
        mutationFn: ({formData, mid}: UsePostArchiveProps) =>
            request({
                formData: toSnakeCase(formData),
                locale,
                method: 'POST',
                url: `/ui/client/${mid}/archive/`,
            }),
    });
};

export type UsePostDeleteErrorResponse = Api.Response.Failure;

export type UsePostDeleteProps = {
    formData: ClientContactsDeleteOrganizationApiRequest;
    mid: string;
};

export type UsePostDeleteSuccessResponse =
    Api.Response.Template.SuccessBasicMessage;

export const usePostDeleteOrganization = (): UseMutationResult<
    UsePostDeleteSuccessResponse,
    UsePostDeleteErrorResponse,
    UsePostDeleteProps
> => {
    const locale = useLocale();

    return useMutation({
        mutationFn: ({formData, mid}: UsePostDeleteProps) =>
            request({
                formData: toSnakeCase(formData),
                locale,
                method: 'POST',
                url: `/ui/client/${mid}/delete/`,
            }),
    });
};

export type UsePostSetDefaultContactsProps = {
    clientMid: string;
    contactId: string;
    formData: ClientContactsSetDefaultApiRequest;
};

export type UsePostSetDefaultErrorResponse = Api.Response.Failure;

export type UsePostSetDefaultSuccessResponse =
    Api.Response.Template.SuccessBasicMessage;

export const usePostSetDefaultContacts = (): UseMutationResult<
    UsePostSetDefaultSuccessResponse,
    UsePostSetDefaultErrorResponse,
    UsePostSetDefaultContactsProps
> => {
    const locale = useLocale();

    return useMutation({
        mutationFn: ({
            clientMid,
            contactId,
            formData,
        }: UsePostSetDefaultContactsProps) =>
            request({
                formData: toSnakeCase(formData),
                locale,
                method: 'POST',
                url: `/ui/client/${clientMid}/contact/${contactId}/make-default/`,
            }),
    });
};

export type UsePostRemoveContactsErrorResponse = Api.Response.Failure;

export type UsePostRemoveContactsProps = {
    contactId: string;
    formData: ClientContactsRemoveContactApiRequest;
};

export type UsePostRemoveContactsSuccessResponse =
    Api.Response.Template.SuccessBasicMessage;

export const usePostRemoveContacts = (
    clientMid: string,
): UseMutationResult<
    UsePostRemoveContactsSuccessResponse,
    UsePostRemoveContactsErrorResponse,
    UsePostRemoveContactsProps
> => {
    const locale = useLocale();

    return useMutation({
        mutationFn: ({contactId, formData}: UsePostRemoveContactsProps) =>
            request({
                formData: toSnakeCase(formData),
                locale,
                method: 'POST',
                url: `/ui/client/${clientMid}/contact/${contactId}/delete/`,
            }),
    });
};
